import type { FC, ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import { useMixpanel } from 'react-mixpanel-browser'
import { useLocation } from 'react-router-dom'
import { NAVIGATION, MAIN_MENU } from '../constants/navigation'
import { Sidebar } from '@homeviews/dashboard-ui'
import { IconButton, PreviewDrawer } from '@homeviews/shared-ui'
import type { ITheme } from '@homeviews/styles'
import {
  getUser,
  toggleUpgradeForm,
  getUpgradeFormState,
  companiesApi,
  useGetUserQuery,
} from '@homeviews/store'
import filterMainMenu from '../helpers/filterMainMenu'
import { UpgradeForm } from '@homeviews/auth'
import { HAS_SIMPLIFIED_NAVIGATION } from '@homeviews/users'

declare const window: any

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      minHeight: '100vh',
      display: 'flex',
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        paddingTop: 64,
      },
    },
    mainWrapper: {
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: 'calc(100vw - 250px)',
      },
      'padding-bottom': '100px',
    },
    hiddenSidebar: {
      '& .c-appBar': {
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      },
      '& .c-sidebar__toggleButton': {
        display: 'none',
      },
    },
    rootNoSidebar: {
      paddingLeft: 0,
      '& .l-mainWrapper': {
        width: '100%!important',
      },
    },
    appBar: {
      backgroundColor: theme.colors.brandDarkGrey,
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
        top: 0,
        width: '100%',
        left: 0,
      },
    },
    menuButton: {},
    logoWrapper: {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      '& img': {
        width: '144px',
      },
    },
    bgImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      zIndex: -1,
    },
  }),
)
interface IProps {
  children: ReactNode
  showSidebar?: boolean
}

const AppLayout: FC<IProps> = ({ children, showSidebar = true }) => {
  const classes = useStyles()
  const location = useLocation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const mixpanel = useMixpanel()
  const upgradeFormOpened = useSelector(getUpgradeFormState)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [sidebarOpened, setSidebarOpened] = useState<boolean>(false)

  if (!window.homeviewsMixpanel) {
    window.homeviewsMixpanel = mixpanel
  }

  // Prefetch company status for the Home/"Get Started" page (trackCompanyProgress).
  // Intentionally not gated by the simplified navigation flag: Home consumes this
  // even when the sidebar onboarding widget is hidden.
  useEffect(() => {
    if (!user?.developer_id) {
      return
    }
    dispatch(
      companiesApi.endpoints.getCompanyStatus.initiate(user.developer_id),
    )
  }, [user, dispatch])

  useEffect(() => {
    if (sidebarOpened) {
      setSidebarOpened(false)
    }
  }, [location, dispatch, sidebarOpened])

  useGetUserQuery()

  const isRouteWithSidebar =
    location.pathname !== NAVIGATION.login.slug &&
    location.pathname !== NAVIGATION.resetPassword.slug

  return (
    <div
      className={clsx(
        classes.root,
        (!isRouteWithSidebar || isMobile) && classes.rootNoSidebar,
        !isRouteWithSidebar && classes.hiddenSidebar,
      )}
    >
      <AppBar position="static" className={clsx(classes.appBar, 'c-appBar')}>
        <Toolbar>
          <IconButton
            edge="start"
            className={clsx(classes.menuButton, 'c-sidebar__toggleButton')}
            color="inherit"
            aria-label="menu"
            svgIcon="menu"
            fontSize={27}
            onClick={() => setSidebarOpened(true)}
          />
          <Link to="/" className={classes.logoWrapper}>
            <img
              src="assets/images/homeviews-logo-light.svg"
              alt="HomeViews logo"
            />
          </Link>
        </Toolbar>
      </AppBar>
      {showSidebar && isRouteWithSidebar && (
        <Sidebar
          menuItems={filterMainMenu(user, MAIN_MENU)}
          hideOnboardingProgress={HAS_SIMPLIFIED_NAVIGATION(user)}
          open={sidebarOpened}
          onClose={() => setSidebarOpened(false)}
          variant={isMobile ? 'temporary' : 'persistent'}
        />
      )}
      <div className={clsx(classes.mainWrapper, 'l-mainWrapper')}>
        {children}
      </div>
      <img
        className={classes.bgImage}
        src="assets/images/dashboard-background-mesh.jpg"
        alt="Background"
      />
      {
        <PreviewDrawer
          open={upgradeFormOpened}
          onClose={() => {
            dispatch(toggleUpgradeForm({ open: false }))
          }}
          hideNav
        >
          <UpgradeForm />
        </PreviewDrawer>
      }
    </div>
  )
}

export default AppLayout
